import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, ChevronRight, Lightbulb, Check, X as XIcon } from 'lucide-react';
import RichText from '../components/RichText';
import ShareButtons from '../components/ShareButtons';
import BlogCard from '../components/BlogCard';
import ProjectIcon from '../components/ProjectIcon';
import { getPost, posts } from '../data/blog';
import './Projects.css';
import './Blog.css';

const SITE = 'https://www.pidesk.in';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

const slugify = (text) =>
  text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Rough reading-time-style word count for the Article schema; not shown to
// readers, just fed to search engines as `wordCount`.
const countWords = (body) =>
  body.reduce((total, b) => {
    const text = b.h2 || b.h3 || b.p || b.quote || (b.ul ? b.ul.join(' ') : '');
    return total + text.split(/\s+/).filter(Boolean).length;
  }, 0);

const Block = ({ block, post }) => {
  if (block.h2) {
    const id = slugify(block.h2);
    return <h2 id={id}><RichText text={block.h2} /></h2>;
  }
  if (block.h3) return <h3><RichText text={block.h3} /></h3>;
  if (block.quote) return <blockquote><RichText text={block.quote} /></blockquote>;
  if (block.image && post.inlineImage) {
    return (
      <figure className="post-figure">
        <img
          src={post.inlineImage.src}
          alt={post.inlineImage.alt}
          width="1200"
          height="auto"
          loading="lazy"
        />
        {post.inlineImage.caption && <figcaption>{post.inlineImage.caption}</figcaption>}
      </figure>
    );
  }

  // ---- Richer "guide"-style blocks, used by in-depth / buyer's-guide posts ----

  // A row of big numbers, e.g. years of uptime, students served.
  if (block.stats) {
    return (
      <div className="stat-strip">
        {block.stats.map((s) => (
          <div key={s.label} className="stat-cell">
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    );
  }

  // A diagram-like grid of feature cards, each with an icon badge.
  if (block.features) {
    return (
      <div className="feature-diagram">
        {block.features.map((f) => (
          <div key={f.title} className="feature-diagram-card">
            <div className="feature-diagram-icon"><ProjectIcon name={f.icon} size={20} /></div>
            <h3>{f.title}</h3>
            <p><RichText text={f.desc} /></p>
          </div>
        ))}
      </div>
    );
  }

  // A real, crawlable comparison table (not an image), e.g. us vs. a generic
  // off-the-shelf option vs. doing nothing.
  if (block.compareTable) {
    const { cols, rows } = block.compareTable;
    return (
      <div className="compare-wrap">
        <table className="compare-table">
          <thead>
            <tr>
              <th scope="col"></th>
              {cols.map((c, i) => <th scope="col" key={c} className={i === 0 ? 'col-us' : ''}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature}>
                <th scope="row">{r.feature}</th>
                {r.values.map((v, i) => (
                  <td key={i} className={i === 0 ? 'col-us' : ''}>
                    {v ? <Check size={16} className="yes" /> : <XIcon size={16} className="no" />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // A highlighted call-to-action band inline in the article, not just at the end.
  if (block.cta) {
    return (
      <div className="inline-cta">
        <div>
          <h3>{block.cta.title}</h3>
          <p>{block.cta.desc}</p>
        </div>
        <Link to={block.cta.to || '/contact'} className="btn btn-primary">
          {block.cta.buttonText || 'Start a project'} <ArrowRight size={16} style={{ marginLeft: 8 }} />
        </Link>
      </div>
    );
  }

  if (block.ul) {
    return (
      <ul>
        {block.ul.map((item, i) => <li key={i}><RichText text={item} /></li>)}
      </ul>
    );
  }
  return <p><RichText text={block.p} /></p>;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const sorted = useMemo(() => [...posts].sort((a, b) => new Date(a.date) - new Date(b.date)), []);
  const index = sorted.findIndex((p) => p.slug === slug);
  const prevPost = index > 0 ? sorted[index - 1] : null;
  const nextPost = index >= 0 && index < sorted.length - 1 ? sorted[index + 1] : null;

  const toc = useMemo(
    () => (post ? post.body.filter((b) => b.h2).map((b) => ({ text: b.h2, id: slugify(b.h2) })) : []),
    [post]
  );

  // Same-category posts first; if that category is thin, pad up to 3 with
  // the newest other posts so "Read next" never shows a single lonely card.
  const related = useMemo(() => {
    if (!post) return [];
    const sameCategory = posts.filter((p) => p.slug !== post.slug && p.category === post.category);
    if (sameCategory.length >= 3) return sameCategory.slice(0, 3);
    const rest = posts
      .filter((p) => p.slug !== post.slug && p.category !== post.category)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return [...sameCategory, ...rest].slice(0, 3);
  }, [post]);

  if (!post) {
    return (
      <div className="page-shell">
        <div className="container page-hero">
          <h1 className="page-title">Post not found</h1>
          <p className="page-subtitle">This article doesn't exist or has moved.</p>
          <Link to="/blog" className="btn btn-primary">Back to the blog</Link>
        </div>
      </div>
    );
  }

  const url = `${SITE}/blog/${post.slug}`;
  const coverUrl = `${SITE}${post.cover}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    wordCount: countWords(post.body),
    author: { '@type': 'Organization', name: 'Pidesk', url: SITE },
    publisher: { '@type': 'Organization', name: 'Pidesk', logo: { '@type': 'ImageObject', url: `${SITE}/icon-512.png` } },
    mainEntityOfPage: url,
    image: [coverUrl],
    keywords: post.tags.join(', ')
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: url }
    ]
  };
  const faqSchema = post.faqs && post.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  } : null;

  return (
    <>
      <>
        <title>{`${post.seoTitle || post.title} | Pidesk Blog`}</title>
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.seoTitle || post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={coverUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:section" content={post.category} />
        {post.tags.map((t) => <meta key={t} property="article:tag" content={t} />)}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seoTitle || post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt} />
        <meta name="twitter:image" content={coverUrl} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </>

      <div className="page-shell">
        <div className="container post-wrap">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><ChevronRight size={13} />
            <Link to="/blog">Blog</Link><ChevronRight size={13} />
            <span>{post.category}</span>
          </nav>

          <span className={`blog-card-cat standalone`}>{post.category}</span>
          <h1 className="post-title">{post.title}</h1>
          <div className="blog-card-meta post-meta-row">
            <span><Calendar size={13} /> {formatDate(post.date)}</span>
            <span><Clock size={13} /> {post.readTime}</span>
          </div>

          {/* Featured image — this doubles as the per-post social share image */}
          <img
            className="post-cover"
            src={post.cover}
            alt={post.coverAlt}
            width="1200"
            height="630"
            loading="eager"
            fetchPriority="high"
          />

          <div className="post-layout">
            <div className="post-main">
              {post.takeaways && post.takeaways.length > 0 && (
                <div className="takeaways-box">
                  <h2><Lightbulb size={17} /> Key takeaways</h2>
                  <ul>
                    {post.takeaways.map((t, i) => <li key={i}><RichText text={t} /></li>)}
                  </ul>
                </div>
              )}

              {toc.length > 1 && (
                <nav className="toc" aria-label="Table of contents">
                  <h2>On this page</h2>
                  <ol>
                    {toc.map((t) => <li key={t.id}><a href={`#${t.id}`}>{t.text}</a></li>)}
                  </ol>
                </nav>
              )}

              <article className="post-body">
                {post.body.map((block, i) => <Block key={i} block={block} post={post} />)}
              </article>

              {post.faqs && post.faqs.length > 0 && (
                <section className="faq-section">
                  <h2>Frequently asked questions</h2>
                  {post.faqs.map((f) => (
                    <details key={f.q} className="faq-item">
                      <summary>{f.q}</summary>
                      <p>{f.a}</p>
                    </details>
                  ))}
                </section>
              )}

              <div className="post-tags">
                {post.tags.map((t) => <span key={t}>{t}</span>)}
              </div>

              <ShareButtons url={url} title={post.title} />

              <div className="author-box">
                <div className="author-avatar">P</div>
                <div>
                  <strong>Written by the Pidesk team</strong>
                  <p>We design and build the ERP, exam and learning products this blog writes about. <Link to="/about">More about us →</Link></p>
                </div>
              </div>

              {(prevPost || nextPost) && (
                <div className="post-pager">
                  {prevPost ? (
                    <Link to={`/blog/${prevPost.slug}`} className="pager-link prev">
                      <span className="pager-label"><ArrowLeft size={13} /> Previous</span>
                      <strong>{prevPost.title}</strong>
                    </Link>
                  ) : <span />}
                  {nextPost && (
                    <Link to={`/blog/${nextPost.slug}`} className="pager-link next">
                      <span className="pager-label">Next <ArrowRight size={13} /></span>
                      <strong>{nextPost.title}</strong>
                    </Link>
                  )}
                </div>
              )}
            </div>

            <aside className="post-side">
              <div className="side-box">
                <h3>Build this with Pidesk</h3>
                <p>We build the ERP, exam and learning platforms this blog covers.</p>
                <Link to="/contact" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Start a project</Link>
              </div>
              {related.length > 0 && (
                <div className="side-box">
                  <h3>More to read</h3>
                  <ul className="other-roles">
                    {related.map((p) => (
                      <li key={p.slug}>
                        <Link to={`/blog/${p.slug}`}>
                          <span>{p.title}</span>
                          <ArrowRight size={14} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>

          {related.length > 0 && (
            <section className="detail-section">
              <h2>Read next</h2>
              <div className="blog-grid">
                {related.map((p) => <BlogCard key={p.slug} post={p} />)}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPost;
